import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Security,
  AdminPanelSettings,
  Business,
  Engineering,
  ShoppingCart,
  ExpandMore,
  People,
  Settings,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  userCount: number;
  isSystem: boolean;
  createdAt: string;
}

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

const permissionCategories = {
  admin: {
    name: 'Administration',
    icon: <AdminPanelSettings />,
    permissions: [
      { id: 'admin:all', name: 'Full System Access', description: 'Complete access to all system features' },
      { id: 'admin:users', name: 'User Management', description: 'Create, edit, and delete users' },
      { id: 'admin:roles', name: 'Role Management', description: 'Manage user roles and permissions' },
      { id: 'admin:permissions', name: 'Permission Management', description: 'Manage system permissions' },
      { id: 'admin:system', name: 'System Settings', description: 'Access to system configuration' },
      { id: 'admin:content', name: 'Content Management', description: 'Manage website content and pages' },
      { id: 'admin:analytics', name: 'Analytics & Reports', description: 'Access to system analytics' },
      { id: 'admin:payments', name: 'Payment Management', description: 'Manage payment gateways and transactions' },
    ]
  },
  production: {
    name: 'Production',
    icon: <Engineering />,
    permissions: [
      { id: 'manager:production', name: 'Production Management', description: 'Manage production lines and processes' },
      { id: 'worker:production', name: 'Production Tasks', description: 'Access to production tasks and status' },
      { id: 'admin:production', name: 'Production Administration', description: 'Full production system access' },
    ]
  },
  inventory: {
    name: 'Inventory',
    icon: <Business />,
    permissions: [
      { id: 'manager:inventory', name: 'Inventory Management', description: 'Manage inventory and materials' },
      { id: 'worker:inventory', name: 'Inventory Access', description: 'View inventory levels and status' },
      { id: 'admin:inventory', name: 'Inventory Administration', description: 'Full inventory system access' },
    ]
  },
  quality: {
    name: 'Quality Control',
    icon: <Security />,
    permissions: [
      { id: 'manager:quality', name: 'Quality Management', description: 'Manage quality control processes' },
      { id: 'worker:quality', name: 'Quality Tasks', description: 'Perform quality inspections' },
      { id: 'admin:quality', name: 'Quality Administration', description: 'Full quality system access' },
    ]
  },
  orders: {
    name: 'Orders',
    icon: <ShoppingCart />,
    permissions: [
      { id: 'manager:orders', name: 'Order Management', description: 'Manage customer orders' },
      { id: 'customer:orders', name: 'Customer Orders', description: 'Place and manage own orders' },
      { id: 'admin:orders', name: 'Order Administration', description: 'Full order system access' },
    ]
  },
  customers: {
    name: 'Customers',
    icon: <People />,
    permissions: [
      { id: 'manager:customers', name: 'Customer Management', description: 'Manage customer accounts' },
      { id: 'customer:profile', name: 'Customer Profile', description: 'Manage own profile' },
      { id: 'admin:customers', name: 'Customer Administration', description: 'Full customer system access' },
    ]
  },
  maintenance: {
    name: 'Maintenance',
    icon: <Settings />,
    permissions: [
      { id: 'manager:maintenance', name: 'Maintenance Management', description: 'Manage maintenance requests' },
      { id: 'worker:maintenance', name: 'Maintenance Tasks', description: 'Access to maintenance tasks' },
      { id: 'admin:maintenance', name: 'Maintenance Administration', description: 'Full maintenance system access' },
    ]
  },
  reports: {
    name: 'Reports',
    icon: <Visibility />,
    permissions: [
      { id: 'manager:reports', name: 'Manager Reports', description: 'Access to management reports' },
      { id: 'worker:reports', name: 'Worker Reports', description: 'Access to worker reports' },
      { id: 'admin:reports', name: 'Admin Reports', description: 'Access to all system reports' },
    ]
  },
  payments: {
    name: 'Payments',
    icon: <ShoppingCart />,
    permissions: [
      { id: 'customer:payments', name: 'Customer Payments', description: 'Make payments for orders' },
      { id: 'manager:payments', name: 'Payment Management', description: 'Manage payment processes' },
      { id: 'admin:payments', name: 'Payment Administration', description: 'Full payment system access' },
    ]
  },
  notifications: {
    name: 'Notifications',
    icon: <Visibility />,
    permissions: [
      { id: 'customer:notifications', name: 'Customer Notifications', description: 'Receive order notifications' },
      { id: 'manager:notifications', name: 'Manager Notifications', description: 'Receive management notifications' },
      { id: 'admin:notifications', name: 'Admin Notifications', description: 'Receive all system notifications' },
    ]
  },
};

export const RoleManagement: React.FC = () => {
  const { hasPermission } = useRbac();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  
  // Form state - moved before conditional return
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  // Mock data
  const mockRoles: Role[] = [
    {
      id: '1',
      name: 'admin',
      description: 'Full system administrator with all permissions',
      permissions: ['admin:all'],
      userCount: 1,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
    },
    {
      id: '2',
      name: 'manager',
      description: 'Production line manager with supervisory access',
      permissions: [
        'manager:production',
        'manager:inventory',
        'manager:quality',
        'manager:orders',
        'manager:customers',
        'manager:maintenance',
        'manager:reports',
        'manager:payments',
        'manager:notifications',
      ],
      userCount: 3,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
    },
    {
      id: '3',
      name: 'worker',
      description: 'Production line worker with limited access',
      permissions: [
        'worker:production',
        'worker:inventory',
        'worker:quality',
        'worker:maintenance',
        'worker:reports',
      ],
      userCount: 15,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
    },
    {
      id: '4',
      name: 'customer',
      description: 'Customer with order management access',
      permissions: [
        'customer:orders',
        'customer:profile',
        'customer:payments',
        'customer:notifications',
      ],
      userCount: 50,
      isSystem: true,
      createdAt: '2024-01-01T00:00:00Z',
    },
    {
      id: '5',
      name: 'supervisor',
      description: 'Production supervisor with enhanced access',
      permissions: [
        'manager:production',
        'manager:quality',
        'manager:reports',
        'worker:inventory',
        'worker:maintenance',
      ],
      userCount: 5,
      isSystem: false,
      createdAt: '2024-01-10T00:00:00Z',
    },
  ];

  useEffect(() => {
    setRoles(mockRoles);
  }, []);

  // Add error boundary for debugging
  if (!hasPermission('admin:roles')) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          You do not have permission to access this page.
        </Typography>
      </Box>
    );
  }

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description,
      });
      setSelectedPermissions(role.permissions);
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
      });
      setSelectedPermissions([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setError(null);
  };

  const handlePermissionToggle = (permissionId: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSubmit = async () => {
    if (!hasPermission('admin:roles')) {
      setError('You do not have permission to manage roles');
      return;
    }

    if (!formData.name || !formData.description) {
      setError('Please fill in all required fields');
      return;
    }

    if (selectedPermissions.length === 0) {
      setError('Please select at least one permission');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingRole) {
        // Update role
        const updatedRoles = roles.map(role =>
          role.id === editingRole.id
            ? {
                ...role,
                name: formData.name,
                description: formData.description,
                permissions: selectedPermissions,
              }
            : role
        );
        setRoles(updatedRoles);
      } else {
        // Create role
        const newRole: Role = {
          id: Date.now().toString(),
          name: formData.name,
          description: formData.description,
          permissions: selectedPermissions,
          userCount: 0,
          isSystem: false,
          createdAt: new Date().toISOString(),
        };
        setRoles([...roles, newRole]);
      }

      handleCloseDialog();
    } catch (err) {
      setError('Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!hasPermission('admin:roles')) {
      setError('You do not have permission to delete roles');
      return;
    }

    const role = roles.find(r => r.id === roleId);
    if (role?.isSystem) {
      setError('Cannot delete system roles');
      return;
    }

    if (role?.userCount && role.userCount > 0) {
      setError('Cannot delete role with assigned users');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setRoles(roles.filter(role => role.id !== roleId));
    } catch (err) {
      setError('Failed to delete role');
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    totalRoles: roles.length,
    systemRoles: roles.filter(r => r.isSystem).length,
    customRoles: roles.filter(r => !r.isSystem).length,
    totalUsers: roles.reduce((sum, r) => sum + (r.userCount || 0), 0),
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Role Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Roles
              </Typography>
              <Typography variant="h4">{stats.totalRoles}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                System Roles
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.systemRoles}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Custom Roles
              </Typography>
              <Typography variant="h4" color="secondary.main">
                {stats.customRoles}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mb: 3, textAlign: 'right' }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={!hasPermission('admin:roles')}
        >
          Create Role
        </Button>
      </Box>

      {/* Roles Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {role.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {role.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {role.permissions.slice(0, 3).map((permission) => (
                        <Chip
                          key={permission}
                          label={permission}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {role.permissions.length > 3 && (
                        <Chip
                          label={`+${role.permissions.length - 3} more`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {role.userCount || 0} users
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={role.isSystem ? 'System' : 'Custom'}
                      color={role.isSystem ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(role.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Role">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(role)}
                          disabled={!hasPermission('admin:roles')}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Role">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRole(role.id)}
                          disabled={!hasPermission('admin:roles') || role.isSystem || (role.userCount ? role.userCount > 0 : false)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Role Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={!!editingRole?.isSystem}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {Object.entries(permissionCategories).map(([categoryKey, category]) => (
                  <Accordion key={categoryKey}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {category.icon}
                        <Typography sx={{ ml: 1 }}>{category.name}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List dense>
                        {category.permissions && Array.isArray(category.permissions) && category.permissions.map((permission) => (
                          <ListItem key={permission.id} dense>
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={selectedPermissions.includes(permission.id)}
                                onChange={() => handlePermissionToggle(permission.id)}
                                disabled={!!editingRole?.isSystem}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={permission.name}
                              secondary={permission.description}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingRole ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="create role"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog()}
        disabled={!hasPermission('admin:roles')}
      >
        <Add />
      </Fab>
    </Box>
  );
}; 